import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import {StyledTextField} from '../text-field'
import {StyledTextFieldHelperText} from '../text-field-helper-text'
import {StyledSelect} from '../select-field'
import {StyledRmwcSimpleListItem} from '../../lists/simple-list-item'
import '@rmwc/icon/styles'
import '@rmwc/select/styles'

const StyledStyledTextField = styled(StyledTextField)`
  &&&.mdc-text-field--outlined {
    border-radius: 5px 0 0 5px;
  }
  &&&.mdc-text-field--outlined .mdc-notched-outline .mdc-notched-outline__trailing {
    border-radius: 0;
    border-right: none;
  }
  /* fix for overlay issue on trade order */
  /* TODO Adnan: Figure out why we need an overlay for text-field */
  &&&&&.mdc-text-field--outlined .mdc-notched-outline .mdc-notched-outline__notch {
    background: transparent;
  }
`

const StyledStyledSelect = styled(StyledSelect)`
  &&&.mdc-select--outlined {
    border-radius: 0 5px 5px 0;
  }
  &&&.mdc-select--outlined .mdc-notched-outline .mdc-notched-outline__leading {
    border-radius: 0;
  }
  &&& .mdc-select__selected-text {
    box-sizing: border-box;
  }

  &&& .mdc-select__anchor {
    width: 100%;
  }
  &&& .mdc-menu .mdc-list {
    padding: 0.5rem;
  }
`

const StyledStyledTextFieldHelperText = styled(StyledTextFieldHelperText)`
  &&& {
    padding-right: 16px;
    padding-left: 16px;
  }
`

const StyledStyledRmwcSimpleListItem = styled(StyledRmwcSimpleListItem)`
  &&& {
    margin: 0;
    padding: 0;
  }
  &&& .mdc-list-item__graphic {
    margin-left: 0.25rem;
    margin-right: 0.75rem;
  }
  &&&.mdc-list-item--disabled {
    opacity: 0.5;
    cursor: pointer;
  }
`

const DecimalSelectField = ({
  field: {name, value},
  form,
  meta,
  options,
  label,
  placeholder,
  helperText,
  disabled = false,
  readOnly = false,
  handleChange = () => {},
  normalize = (value) => value,
  decimalMaskOptions = {},
  outlined = true,
  ...otherProps
}) => {
  const error = form.errors[name] || meta?.error

  function composeValue(textValue, selectValue) {
    if (!textValue && !selectValue) {
      return null
    } else {
      return [textValue, selectValue]
    }
  }

  const defaultDecimalMaskOptions = {
    prefix: '',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 8,
    allowNegative: false,
    allowLeadingZeroes: false
  }

  const decimalCurrencyMask = createNumberMask({
    ...defaultDecimalMaskOptions,
    ...decimalMaskOptions
  })

  return (
    <>
      <div style={{display: 'flex', alignItems: 'center', width: 'auto'}}>
        <div style={{flexGrow: 1}}>
          <MaskedInput
            mask={decimalCurrencyMask}
            type="text"
            value={value?.[0] || ''}
            label={label}
            invalid={!!error}
            disabled={disabled}
            readOnly={readOnly}
            outlined={outlined}
            onChange={(e) => {
              const prevValue = value
              const val = e.target.value === '' ? null : e.target.value
              const normalizedValue = normalize(composeValue(val, value?.[1] || null))
              form.setFieldValue(name, normalizedValue)
              handleChange(normalizedValue, name, form, prevValue)
            }}
            render={(ref, maskedInputProps) => <StyledStyledTextField inputRef={ref} {...maskedInputProps} />}
            {...otherProps}
          />
        </div>
        <div style={{width: '130px'}}>
          <StyledStyledSelect
            value={options.length && value?.[1] ? `${value[1]}` : ''}
            icon={value?.[1] ? options?.find((o) => o.value === value[1])?.icon : undefined}
            enhanced
            outlined={outlined}
            onChange={(e) => {
              const prevValue = value
              const val = e.target.value === '' ? null : e.target.value
              const normalizedValue = normalize(composeValue(value?.[0] || null, val))
              form.setFieldValue(name, normalizedValue)
              handleChange(normalizedValue, name, form, prevValue)
            }}
            label=""
            placeholder={placeholder}
            invalid={!!error}
            disabled={disabled}
            readOnly={readOnly}
            {...otherProps}>
            {options?.map((o, index) => (
              <StyledStyledRmwcSimpleListItem
                key={index}
                value={o.value}
                text={o.label}
                // disabled={o.disabled}
                style={{
                  opacity: o.disabled ? 0.5 : 1
                }}
                graphic={o.icon ? o.icon : undefined}
              />
            ))}
          </StyledStyledSelect>
        </div>
      </div>
      {error && (
        <StyledStyledTextFieldHelperText persistent validationMsg>
          {error}
        </StyledStyledTextFieldHelperText>
      )}
      {!error && <StyledStyledTextFieldHelperText persistent>{helperText}</StyledStyledTextFieldHelperText>}
    </>
  )
}

DecimalSelectField.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  meta: PropTypes.object,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  handleChange: PropTypes.func,
  normalize: PropTypes.func,
  icon: PropTypes.object,
  outlined: PropTypes.bool,
  decimalMaskOptions: PropTypes.shape({
    prefix: PropTypes.string,
    suffix: PropTypes.string,
    includeThousandsSeparator: PropTypes.bool,
    thousandsSeparatorSymbol: PropTypes.string,
    allowDecimal: PropTypes.bool,
    decimalSymbol: PropTypes.string,
    decimalLimit: PropTypes.string, // how many digits allowed after the decimal
    requireDecimal: PropTypes.bool,
    allowNegative: PropTypes.bool,
    allowLeadingZeroes: PropTypes.bool,
    integerLimit: PropTypes.number // limit length of integer numbers
  })
}

export {DecimalSelectField}
